import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ExternalLink, User, LogOut, ArrowLeft } from 'lucide-react';

const LOGO_URL = "https://cdn.prod.website-files.com/65bab6e8de90f51ce337cb2c/65ca81b04a44869f7f5747ca_LOGO_S2_WHITE_TRANSPARENT.png";

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userType, setUserType] = useState(null); // 'admin' or 'producer'
  const [showUserMenu, setShowUserMenu] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setShowUserMenu(false);
    
    // Check if user is logged in based on current path
    if (location.pathname.startsWith('/admin')) {
      const adminAuth = sessionStorage.getItem('adminAuth');
      if (adminAuth) {
        setIsLoggedIn(true);
        setUserType('admin');
      }
    } else if (location.pathname.startsWith('/producteur/')) {
      setIsLoggedIn(true);
      setUserType('producer');
      // Store producer token for later use
      const token = location.pathname.split('/producteur/')[1];
      if (token) sessionStorage.setItem('producerToken', token);
    } else if (location.pathname.startsWith('/projet/')) {
      setIsLoggedIn(true);
      setUserType('producer');
    } else {
      // Check if coming back from logged in state
      const adminAuth = sessionStorage.getItem('adminAuth');
      const producerToken = sessionStorage.getItem('producerToken');
      if (adminAuth) {
        setIsLoggedIn(true);
        setUserType('admin');
      } else if (producerToken) {
        setIsLoggedIn(true);
        setUserType('producer');
      } else {
        setIsLoggedIn(false);
        setUserType(null);
      }
    }
  }, [location]);

  const handleLogout = () => {
    sessionStorage.removeItem('adminAuth');
    sessionStorage.removeItem('producerToken');
    setIsLoggedIn(false);
    setUserType(null);
    setShowUserMenu(false);
    navigate('/');
  };

  const handleGoToSpace = () => {
    if (userType === 'admin') {
      navigate('/admin');
    } else if (userType === 'producer') {
      const token = sessionStorage.getItem('producerToken');
      if (token) navigate(`/producteur/${token}`);
    }
    setShowUserMenu(false);
  };

  const navLinks = [
    { label: 'Accueil', path: '/' },
    { label: 'Conviction', path: '/conviction' },
    { label: 'Nos projets', path: '/projets' },
    { label: 'Agenda', path: '/agenda' },
    { label: 'Qui sommes-nous', path: '/about' },
    { label: 'Contact', path: '/contact' },
    { label: 'Festival WiPP', path: 'https://wippfestival.com/', external: true },
    { label: 'Cinémaquis', path: 'https://www.instagram.com/cinemaquis/', external: true },
  ];

  const canGoBack = location.pathname !== '/' && (
    location.pathname.startsWith('/projet/') || 
    location.pathname.startsWith('/producteur/') ||
    location.pathname === '/admin'
  );

  return (
    <>
      <motion.header
        data-testid="main-header"
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled ? 'glass-dark py-3' : 'py-6 bg-transparent'
        }`}
      >
        <div className="container-custom flex items-center justify-between">
          <Link to="/" data-testid="logo-link" className="relative z-10">
            <img 
              src={LOGO_URL} 
              alt="Second Souffle Production" 
              className="h-10 md:h-12 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-6" data-testid="desktop-nav">
            {navLinks.map((link) => (
              link.external ? (
                <a
                  key={link.label}
                  href={link.path}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="nav-link text-sm font-medium text-white/80 hover:text-white flex items-center gap-1 whitespace-nowrap"
                  data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {link.label}
                  <ExternalLink size={12} className="opacity-50" />
                </a>
              ) : (
                <Link
                  key={link.label}
                  to={link.path}
                  className={`nav-link text-sm font-medium transition-colors whitespace-nowrap ${
                    location.pathname === link.path 
                      ? 'text-white' 
                      : 'text-white/70 hover:text-white'
                  }`}
                  data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {link.label}
                </Link>
              )
            ))}
          </nav>

          {/* Right Side Actions */}
          <div className="hidden lg:flex items-center gap-4">
            {/* User Menu or Login */}
            {isLoggedIn ? (
              <div className="relative">
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#D4AF37]/20 text-[#D4AF37] hover:bg-[#D4AF37]/30 transition-colors whitespace-nowrap"
                >
                  <User size={16} />
                  <span className="text-sm font-medium">Mon espace</span>
                </button>
                
                {showUserMenu && (
                  <div className="absolute right-0 mt-2 w-56 rounded-xl bg-[#1a1a1a] border border-white/10 shadow-xl overflow-hidden z-50">
                    <button
                      onClick={handleGoToSpace}
                      className="w-full px-4 py-3 text-left text-white hover:bg-white/10 flex items-center gap-2 text-sm"
                    >
                      <User size={14} />
                      {userType === 'admin' ? 'Administration' : 'Mes projets'}
                    </button>
                    <button
                      onClick={handleLogout}
                      className="w-full px-4 py-3 text-left text-red-400 hover:bg-white/10 flex items-center gap-2 text-sm border-t border-white/10"
                    >
                      <LogOut size={14} />
                      Se déconnecter
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link
                to="/connexion"
                className="text-sm font-medium text-white/70 hover:text-white transition-colors whitespace-nowrap"
              >
                Se connecter
              </Link>
            )}
            
            <Link
              to="/faire-un-don"
              className="btn-primary text-sm whitespace-nowrap"
              data-testid="donate-btn-header"
            >
              Faire un don
            </Link>

            {/* Back Button (for logged in users) */}
            {canGoBack && (
              <button
                onClick={() => navigate(-1)}
                className="flex items-center gap-1 text-white/70 hover:text-white transition-colors whitespace-nowrap"
              >
                <ArrowLeft size={16} />
                <span className="text-sm">Retour</span>
              </button>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden relative z-10 p-2 text-white"
            data-testid="mobile-menu-btn"
            aria-label="Menu"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </motion.header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 lg:hidden"
            data-testid="mobile-menu"
          >
            <div 
              className="absolute inset-0 bg-black/80 backdrop-blur-xl"
              onClick={() => setIsMobileMenuOpen(false)}
            />
            <motion.nav
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'tween', duration: 0.3 }}
              className="absolute right-0 top-0 bottom-0 w-72 bg-[#0a0a0c] border-l border-white/10 p-8 pt-24"
            >
              <div className="flex flex-col gap-6">
                {navLinks.map((link) => (
                  link.external ? (
                    <a
                      key={link.label}
                      href={link.path}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-lg font-medium text-white/80 hover:text-white flex items-center gap-2"
                      data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {link.label}
                      <ExternalLink size={14} className="opacity-50" />
                    </a>
                  ) : (
                    <Link
                      key={link.label}
                      to={link.path}
                      className={`text-lg font-medium ${
                        location.pathname === link.path 
                          ? 'text-[#D4AF37]' 
                          : 'text-white/80 hover:text-white'
                      }`}
                      data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {link.label}
                    </Link>
                  )
                ))}
                <Link
                  to="/faire-un-don"
                  className="btn-primary text-center mt-4"
                  data-testid="mobile-donate-btn"
                >
                  Faire un don
                </Link>
              </div>
            </motion.nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
